import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PawPrint } from 'lucide-react';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { authApi } from '../../../api/auth.js';
import { ApiError } from '../../../api/client.js';
import { env } from '../../../lib/env.js';

/**
 * Public claim screen. An invited owner with no account yet sets their own
 * password from the invite link (?token=...). On success the account is
 * provisioned, linked to their customer and a session is opened, landing them
 * straight in the portal. No prior login required.
 */
export function ClaimPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { claimAccount } = useAuth();

  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState(null);

  const inviteQuery = useQuery({
    queryKey: ['invite', token],
    queryFn: () => authApi.getInvite(token),
    enabled: Boolean(token),
    retry: false
  });

  const claimMut = useMutation({
    mutationFn: () => claimAccount({ token, password }),
    onSuccess: () => navigate('/account', { replace: true })
  });

  const inviteInvalid = inviteQuery.isError;
  const policyOk = password.length >= 12 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password);

  const submit = (e) => {
    e.preventDefault();
    setLocalError(null);
    if (!policyOk) { setLocalError('La password deve avere almeno 12 caratteri, con maiuscola, minuscola e numero.'); return; }
    if (password !== confirm) { setLocalError('Le password non coincidono.'); return; }
    claimMut.mutate();
  };

  const serverError = claimMut.isError
    ? (claimMut.error instanceof ApiError ? claimMut.error.message : 'Qualcosa è andato storto. Riprova.')
    : null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-portal-bg px-6 text-center">
      <div className="w-full max-w-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-portal-ink-faint">{env.appName}</p>
        <PawPrint className="mx-auto mt-6 h-12 w-12 text-portal-brand" strokeWidth={1.5} />
        <h1 className="mt-4 text-2xl font-semibold text-portal-ink">Attiva il tuo spazio</h1>

        {!token && (
          <p className="mt-3 text-portal-ink-muted">Link non valido. Chiedi un nuovo invito in negozio.</p>
        )}

        {token && inviteQuery.isLoading && (
          <p className="mt-6 text-portal-ink-muted">Verifico l'invito…</p>
        )}

        {token && inviteInvalid && (
          <p className="mt-3 text-portal-ink-muted">
            Invito non valido o scaduto. Chiedi un nuovo link in negozio.
          </p>
        )}

        {token && inviteQuery.isSuccess && (
          <>
            <p className="mt-2 text-portal-ink-muted">
              Scegli una password per accedere allo spazio del tuo animale.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3 text-left">
              <div>
                <label className="text-sm font-medium text-portal-ink">Email</label>
                <input
                  type="email"
                  value={inviteQuery.data.email || ''}
                  readOnly
                  className="mt-1 w-full rounded-portal border border-portal-border bg-portal-bg-soft px-4 py-3 text-portal-ink-muted outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-portal-ink">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Almeno 12 caratteri"
                  className="mt-1 w-full rounded-portal border border-portal-border bg-portal-card px-4 py-3 text-portal-ink outline-none focus:border-portal-brand"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-portal-ink">Conferma password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1 w-full rounded-portal border border-portal-border bg-portal-card px-4 py-3 text-portal-ink outline-none focus:border-portal-brand"
                />
              </div>

              {(localError || serverError) && (
                <p className="text-sm text-portal-brand">{localError || serverError}</p>
              )}

              <button
                type="submit"
                disabled={claimMut.isPending || !password || !confirm}
                className="w-full rounded-full bg-portal-brand px-6 py-3 font-medium text-white hover:bg-portal-brand-hover disabled:opacity-50"
              >
                {claimMut.isPending ? 'Attivo…' : 'Attiva accesso'}
              </button>
            </form>
          </>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="mt-6 text-sm text-portal-ink-muted hover:text-portal-brand"
        >
          Ho già un accesso
        </button>
      </div>
    </div>
  );
}
